import type { WellIndexType, WellLogsWidgetState, WidgetStateParams } from '@go-widgets/well-logs-widget';
import { action, makeObservable, observable } from 'mobx';

import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { WidgetWithEditMode } from 'src/entities/widget/WidgetWithEditMode';
import { requireService } from 'src/packages/di';

interface WellLogsWidgetOptions {
  type: string;
  stateParams: Partial<WidgetStateParams>;
  wellIndexType: WellIndexType;
  isEditMode: boolean;
  wellId: number | null;
}

export type TWellLogsSerializedInternalState = {
  stateParams: Partial<WidgetStateParams>;
  wellIndexType: WellIndexType;
  isEditMode: boolean;
  wellId: number | null;
};

export class WellLogsWidgetEntity extends WidgetWithEditMode {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: {
      w: 150,
      h: 150,
    },
    maxInPixels: null,
    min: null,
    max: {
      w: 256,
      h: 100,
    },
  };

  @observable state: WellLogsWidgetState | null = null;
  @observable type: string;
  @observable wellIndexType: WellIndexType;
  @observable isEditMode: boolean;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<WidgetStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;

  constructor(
    { type, wellIndexType, stateParams, isEditMode, wellId }: WellLogsWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);

    this.type = type;
    this.wellIndexType = wellIndexType;
    this.isEditMode = isEditMode;
    this.wellId = wellId;
    this.stateParams = stateParams;

    makeObservable(this);
  }

  getNameT(): string {
    if (this.wellIndexType === 'LOG_DEPTH') {
      return 'settings:Widgets.byDepth';
    }

    return 'settings:Widgets.byTime';
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setWellboreName(name: string): void {
    this.wellboreName = name;
  }

  @action.bound
  setStateParams(stateParams: Partial<WidgetStateParams>): void {
    this.stateParams = stateParams;
  }

  get isControlWidget(): boolean {
    return false;
  }

  get title(): string {
    const index = this.i18.t(this.wellIndexType === 'LOG_DEPTH' ? 'widgets:depth' : 'widgets:time');
    return `${this.i18.t('widgets:WellLogs.title')} (${index})`;
  }

  @action.bound
  setState(state: WellLogsWidgetState | null): void {
    this.state = state;
  }

  @action.bound
  setEditMode(value: boolean): void {
    this.isEditMode = value;

    if (value && !this.isFullscreen) {
      this.setFullscreen(true);
    }
  }

  @action.bound
  setFullscreen(value: boolean): void {
    this.isFullscreen = value;
    this.state?.setAppParams({
      fullScreen: value,
    });
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
    this.state?.setAppParams({
      active: this.inActiveTab,
    });
  }

  override destroy() {
    this.state?.destroy();
  }
}
